<template>
	<div
		v-if="!(error || computedNoNetwork)"
		class="infoboard"
	>
		<div
			class="infoboard__background"
			:style="computedBackgroundStyle"
		>
			<svg v-if="infoboard.greyscale !== undefined && infoboard.backgroundBlurFactor !== undefined && computedSupportsFilter">
				<filter id='sharpBlur' color-interpolation-filters="sRGB">
					<feGaussianBlur :stdDeviation="infoboard.backgroundBlurFactor"></feGaussianBlur>
					<feColorMatrix type='matrix' values='1 0 0 0 0, 0 1 0 0 0, 0 0 1 0 0, 0 0 0 9 0'></feColorMatrix>
					<feColorMatrix type="saturate" :values="String(1 - infoboard.greyscale / 100)"/>
				</filter>
			</svg>
		</div>
		<template v-if="!infoboard.disabled">

			<div
				v-if="computedCurrentRotation"
				class="infoboard__grid"
			>
				<div class="infoboard__grid__element infoboard__grid__element--company">
					<InfoboardCompany
						:type="computedType"
						:rotation-name="computedCurrentRotationObject?.title || ''"
						:text-field="computedCurrentRotationObject?.textField || ''"
						:rotation-type="computedCurrentRotationObject?.rotationType || 'single'"
						:logo="infoboard.companyLogo"
					/>
				</div>

				<div class="infoboard__grid__element infoboard__grid__element--chart">
					<InfoboardChart
						:class="{'hide-chart': !computedChartHasData}"
						:rotation="computedCurrentRotation"
						:locale="infoboard.locale"
					/>
					<StackedIconInformation
						v-if="!computedChartHasData"
						class="g-text g-text--l g-text--center no-data"
						:icon="['fal', 'chart-bar']"
					>
						{{ $t('sharedTexts.noData') }}
					</StackedIconInformation>

					<div
						v-if="computedOldData"
						class="infoboard__error"
					>
						<FontAwesomeIcon
							:icon="['fal', 'exclamation-triangle']"
						/>
						{{ $t('sharedTexts.networkError') }}
					</div>
					<div
						v-if="computedTimezoneWarning"
						class="infoboard__error"
					>
						<FontAwesomeIcon
							:icon="['fal', 'exclamation-triangle']"
						/>
						{{ $t('sharedTexts.timezoneWarning') }}
					</div>

					<div
						v-if="rotations.length > 1"
						class="infoboard__dots"
					>
						<div
							v-for="count in rotations.length"
							:key="count"
							@click.stop="setInfoboardCount(count - 1)"
						>
							<FontAwesomeIcon
								:icon="['fad', 'circle']"
								:style="{'--fa-secondary-opacity': count - 1 === currentRotationIndex ? 0.5 : 0}"
							/>
						</div>
					</div>
				</div>

				<div class="infoboard__grid__element infoboard__grid__element--percentage">
					<InfoboardDescription
						v-if="computedCurrentRotation.description && computedChartHasData"
						:rotation="computedCurrentRotation"
						:locale="infoboard.locale"
					/>
				</div>
			</div>
			<div
				v-else
				class="g-window g-window--border-only"
			>
				<div class="g-text g-text--center g-text--l">
					<FontAwesomeIcon
						class="fa-spin"
						:icon="['fad', 'spinner-third']"
					/>
				</div>
				{{ $t('sharedTexts.loading') }}
			</div>

			<div class="infoboard__logo">
				{{ $t('sharedTexts.poweredBy') }}
			</div>
		</template>
		<template v-else>
			<div class="g-window g-window--border-only g-text g-text--l g-text--center">
				<span class="fa-stack fa-2x">
					<StackedIconInformation :icon="['fad', 'plug']" />
				</span>
				<div>
					<div class="g-container g-text g-text--xs g-text--center">
						{{ $t('sharedTexts.disabled') }}
					</div>
				</div>
			</div>
		</template>
	</div>

	<div
		v-else
		class="infoboard infoboard--error"
	>
		<div class="g-window g-window--border-only g-text g-text--l g-text--center">
			<StackedIconInformation :icon="computedError.icon" />
			<div>
				<h1>{{ $t('sharedTexts.error') }}</h1>
				<div class="g-container">
					<p class="g-text g-text--xs g-text--center">
						{{ computedError.text }}
					</p>
				</div>
			</div>
		</div>
	</div>
	<button
		class="sign-out"
		:title="$t('sharedTexts.logout')"
		@click.stop="signOutClicked"
	>
		<FontAwesomeIcon :icon="['fal', 'sign-out-alt']" />
	</button>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { store } from '@libs/store'
import { storage } from '@libs/utils'

import {
	Rotation,
	Company,
	Infoboard,
	RotationType
} from '@libs/interfaces/interfaces'

import InfoboardCompany from '@/components/InfoboardCompany.vue'
import InfoboardDescription from '@/components/InfoboardDescription.vue'
import InfoboardChart from '@/components/InfoboardChart.vue'

import StackedIconInformation from '@/components/StackedIconInformation.vue'

import {
	getInfoboard,
	getRotation,
	pingInfoboard
} from '@libs/requests'

export default defineComponent({
	name: 'InfoboardView',
	setup() {
		const route = useRoute()
		const router = useRouter()
		return { route, router }
	},
	components: {
		InfoboardCompany,
		InfoboardChart,
		InfoboardDescription,
		StackedIconInformation
	},
	data() {
		return {
			infoboard: {} as Infoboard,
			rotations: {} as Rotation[],
			companyData: {} as Company,

			currentRotationIndex: 0,
			autoChange: true,

			error: '',
			errorCount: 0,
			errorLoad: false,

			rotateInterval: 0 as unknown as number,
			fetchInterval: 0 as unknown as number,
			updateInterval: 0 as unknown as number,
			timesRotated: 0,
			currentVersion: 0
		}
	},
	computed: {
		computedChartHasData(): boolean {
			// Zero consumption (e.g. heat in summer) is valid data and should still show the chart
			return (this.computedCurrentRotation?.chart.currentData?.length ?? 0) > 0
		},
		computedError(): { icon: string[]; text: string} {
			return {
				icon: this.errorLoad ? ['fal', 'exclamation-triangle'] : ['fal', 'wifi'],
				text: this.errorLoad ? this.$t('sharedTexts.loadFatal') : this.$t('sharedTexts.networkErrorFatal')
			}
		},
		computedBackgroundStyle(): Record<string, string> {
			// background will be white if filter points to an invalid ID
			const style: Record<string, string> = {}
			
			if (this.infoboard.background) {
				style['background-image'] = `url('${this.infoboard.background}')`
			}
			
			style.filter = this.computedSupportsFilter ? 'url("#sharpBlur")' : 'none'
			
			return style
		},

		computedSupportsFilter() {
			const niceSupport = store.getData('featureSupport.nice') as Record<string, unknown>[]
			const feColorMatrix = niceSupport.find(feature => feature.name === 'feColorMatrix')

			return feColorMatrix ? feColorMatrix.support : false
		},

		computedNoNetwork(): boolean {
			const hasInfoboard = this.infoboard && Object.keys(this.infoboard).length
			return (!hasInfoboard && this.errorCount > 0) || this.errorCount >= 2
		},

		computedOldData(): boolean {
			return this.errorCount > 0
		},
		computedTimezoneWarning(): boolean {
			if (!this.computedCurrentRotation) return false
			return this.computedCurrentRotation.timezoneWarning
		},

		computedCurrentRotation(): null | Rotation {
			if (this.rotations.length === 0) return null
			const rotation = this.rotations[this.currentRotationIndex]
			return rotation ?? null
		},

		computedCurrentRotationObject(): null | { title: string; textField: string; rotationType: RotationType } {
			if (!this.infoboard.rotationObjects || this.infoboard.rotationObjects.length === 0) return null
			const obj = this.infoboard.rotationObjects[this.currentRotationIndex]
			return obj ?? null
		},

		computedType(): string {
			return this.computedCurrentRotation
				? this.computedCurrentRotation.rotationType === 'single' ? this.computedCurrentRotation.energyType as string : this.computedCurrentRotation.rotationType
				: ''
		}
	},
	async mounted(): Promise<void> {
		if (this.route.query.auto === 'false') this.autoChange = false
		if (this.route.query.index) {
			this.currentRotationIndex = parseInt(this.route.query.index as string)
		}

		if (!localStorage.getItem('authorization')) {
			console.error('Cannot load board, missing token')
			this.errorCount++
		}

		// Check if cached data is more than 1 hour old and clear it
		const cacheKeys = ['infoboard_cache', 'rotations_cache']
		const shouldClearCache = cacheKeys.some(key => {
			// Check if data is older than 1 hour
			return storage.isOlderThan(key, 1)
		})
		
		if (shouldClearCache) {
			storage.clear(cacheKeys)
		}

		await this.reloadInfoboard()

		if (Object.keys(this.infoboard).length === 0) this.errorLoad = true

		this.resetUpdateInterval()
	},
	methods: {
		setInfoboardCount(count: number): void {
			this.currentRotationIndex = count
			if (this.autoChange) this.resetRotateInterval()
		},

		resetRotateInterval() {
			this.timesRotated++
			clearInterval(this.rotateInterval)

			this.rotateInterval = setInterval(() => {
				this.currentRotationIndex = this.currentRotationIndex === this.rotations.length - 1
					? 0
					: this.currentRotationIndex + 1
			}, this.infoboard.infoboardDuration) as unknown as number
		},

		resetUpdateInterval() {
			clearInterval(this.updateInterval)

			// We need to this, because most smart TVs turn on the same time (and thus hit the API at the same time), and we don't want to overload the API.
			// Random interval between 50-70 minutes (1 hour ± 10 minutes)
			const baseInterval = 60 * 60 * 1000 // 1 hour in milliseconds
			const variation = 10 * 60 * 1000 // 10 minutes in milliseconds
			const randomInterval = baseInterval + (Math.random() * 2 - 1) * variation

			this.updateInterval = setInterval(() => {
				// Clear cache before fetching fresh data
				const cacheKeys = ['infoboard_cache', 'rotations_cache']
				storage.clear(cacheKeys)
				
				this.reloadInfoboard()
			}, randomInterval) as unknown as number
		},

		hardReload() {
			location.reload()
		},

		async getInfoboard(): Promise<Infoboard | null> {
			const response = await getInfoboard()

			if (!response.success) {
				return null
			}

			return response.data as Infoboard
		},

		async getRotation(rotationId: number): Promise<Rotation | null> {
			const response = await getRotation(rotationId)
			if (response.success) {
				return response.data as Rotation
			}
			return null
		},

		async reloadInfoboard(): Promise<void> {
			// Try to load from cache first (only on initial load, not on hourly refresh)
			const cachedInfoboard = storage.get<Infoboard>('infoboard_cache')
			const cachedRotations = storage.get<Rotation[]>('rotations_cache')

			if (cachedInfoboard && cachedRotations) {
				this.infoboard = cachedInfoboard
				this.rotations = cachedRotations
				this.$i18n.locale = cachedInfoboard.locale
				
				if (this.autoChange) this.resetRotateInterval()
				return
			}

			const infoboardData = await this.getInfoboard()

			this.errorLoad = false

			
			if (!(infoboardData)) {
				this.errorCount++
				return
			}
			this.infoboard = infoboardData
			this.$i18n.locale = infoboardData.locale
			if(infoboardData?.disabled) return
			
			const rotationPromises = infoboardData.rotationObjects.map(rotation => this.getRotation(rotation.id))
			const rotationResults = await Promise.all(rotationPromises)
			this.rotations = rotationResults.filter((rotation): rotation is Rotation => rotation !== null)

			// Save successful data to cache
			if (this.rotations.length > 0) {
				storage.set('infoboard_cache', this.infoboard)
				storage.set('rotations_cache', this.rotations)
			}

			await pingInfoboard(this.timesRotated)
			this.timesRotated = 0 // Is maintained as a tællerstand in the backend, so it is reset here

			if (this.rotations.length > 0 && this.rotations[0]?.clientVersion && this.currentVersion !== this.rotations[0].clientVersion && this.currentVersion > 0) { // If the backend tells there is a new client version, force-refresh the browser.
				this.currentVersion = this.rotations[0].clientVersion
				this.hardReload()
			}
			if (this.rotations.length > 0 && this.rotations[0]?.clientVersion) {
				this.currentVersion = this.rotations[0].clientVersion
			}

			if (this.autoChange) this.resetRotateInterval()
			this.errorCount = 0
		},

		signOutClicked() {
			// Clear cached data when signing out
			storage.clear(['authorization', 'current_pin', 'infoboard_cache', 'rotations_cache'])
			this.router.push('/')
		}
	}
})
</script>

<style lang="scss">
.sign-out {
	position: fixed;
	bottom: 20px;
	left: 20px;
	color: #fff;
	font-size: 26px;
	opacity: 0.5;
	background: transparent;
	border: none;
	cursor: pointer;

	&:hover {
		opacity: 1;
	}
}

.infoboard {
	color: $color-white;
	height: 100vh;

	.g-container {
		text-align: center;
	}

	.hide-chart {
		opacity: 0;
		pointer-events: none;
	}

	.no-data {
		left: 50%;
		white-space: nowrap;
		text-align: center;
		padding: 20px;
		border-radius: $base-border-radius;
		width: auto;
		font-size: 16px;
	}

	&--error {
		background: $color-grey;
	}

	&__disabled {
		background-color: rgba($color-white, 0.6);
		color: $color-black;
		&__icons {
			color: $color-red;
			padding: 20px;
			font-size: 40px;

			svg:last-child {
				color: darken($color-red, 10)
			}
		}

		p {
			padding: 20px;
		}
	}

	&__background {
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		background-repeat: no-repeat;
		background-size: cover;
		background-position: center;
		background-color: $color-grey;
		z-index: -1; // does not seem to be needed? Strange.

		svg {
			height: 0;
			width: 0;
		}
	}

	&__dots {
		display: flex;
		justify-content: center;
		position: absolute;
		left: 50%;
		top: calc(100% + 100px);
		transform: translateX(-50%);
		opacity: 0.7;

		> * {
			padding: 5px;
			cursor: pointer;
		}
	}

	&__error {
		text-align: center;
		transform: translateY(70px);
		margin-bottom: 10px;
		white-space: nowrap;
	}

	&__logo {
		text-align: center;
	}

	&__grid {
		position: relative;
		box-sizing: border-box;

		padding: 0 10vw;

		> * {
			margin: 18vw auto;
		}
	}
}

@media screen and (min-width: $bp-mobile) {
	.infoboard  {
		.g-container {
			text-align: initial;
		}

		&__logo {
			position: absolute;
			bottom: $base-spacing / 2;
			right: $base-spacing / 2;
			transform: scale(0.75) translate3d(12.5%, 12.5%, 0);
		}
	}
}

@media screen and (min-width: $bp-tablet) and (orientation: landscape) {
	.infoboard {
		display: flex;
		flex-direction: column;
		justify-content: center;
		overflow: hidden;

		&__grid {
			box-sizing: border-box;
			display: grid;
			grid-template-columns: 1fr 2fr 1fr;
			gap: 4vw;

			> * {
				margin: 0;
			}

			&__element {
				position: relative;
				box-sizing: border-box;
				display: flex;
				flex-direction: column;
				justify-content: flex-end;


				> * {
					width: 100%;
					margin: 0 auto;
				}
			}

		}

		&__logo {
			text-align: right;
		}
	}
}
</style>
